/**
 * P3 — Camera-PiP layout geometry for proctoring overlay mapping.
 *
 * Proctoring detects on the raw webcam feed, but the saved recording is the
 * screen-share. To draw the detection overlay in the right place at report
 * time, we tell the backend WHERE the on-screen camera tile sits inside the
 * recording (which, for an "entire screen" share, is the physical monitor).
 *
 * This module holds the PURE math so it can be unit-tested without a DOM:
 * given the camera tile's viewport rect and the window's screen metrics, it
 * returns the tile's box as fractions (0..1) of the screen frame.
 *
 * The screen-coordinate mapping is a standard approximation (window origin +
 * top chrome + side border). Fractions are device-pixel-ratio invariant
 * because numerator and denominator are both CSS pixels and the recording
 * scales uniformly. Exact offsets can need live calibration across OS / zoom.
 */
const clamp01 = (v) => Math.max(0, Math.min(1, v));
/**
 * Tile box as a fraction of the VIEWPORT. Both `rect` and the viewport are in
 * the SAME (current-zoom) CSS pixels, so the ratio is invariant under browser
 * zoom, device-pixel-ratio, OS scaling and screen size. Exact whenever the
 * viewport fills the shared surface (fullscreen, or tab/window share).
 */
export function viewportFraction(rect, innerW, innerH) {
    if (!innerW || !innerH || rect.width <= 0 || rect.height <= 0)
        return null;
    return {
        x: clamp01(rect.left / innerW),
        y: clamp01(rect.top / innerH),
        w: clamp01(rect.width / innerW),
        h: clamp01(rect.height / innerH),
    };
}
/**
 * Map a tile's viewport rect to a fraction of the SCREEN recording, WITHOUT
 * needing fullscreen. Built entirely from viewport-relative CSS pixels, so it
 * is zoom-, DPR- and screen-size-invariant:
 *
 *   • Horizontally a maximized window fills the screen, so x/w are viewport
 *     fractions (plus any tiny side border).
 *   • Vertically the browser chrome (address/tab bar) sits above the viewport;
 *     its height ≈ outerHeight − innerHeight, so the tile is offset by it and
 *     the total screen height ≈ innerHeight + chrome.
 *
 * EXACT when the window is maximized (and, as a special case, when fullscreen —
 * chrome ≈ 0 → pure viewport fraction). Approximate only for a floating /
 * non-maximized window or a secondary monitor, where the browser cannot know
 * the window's on-screen position (taskbar/offset unaccounted).
 */
export function viewportToScreenFraction(rect, m) {
    if (!m.innerW || !m.innerH || rect.width <= 0 || rect.height <= 0)
        return null;
    const chromeTop = Math.max(0, (m.outerH || m.innerH) - m.innerH);
    const chromeSide = Math.max(0, ((m.outerW || m.innerW) - m.innerW) / 2);
    const totalH = m.innerH + chromeTop;
    const totalW = m.innerW + chromeSide * 2;
    return {
        x: clamp01((chromeSide + rect.left) / totalW),
        y: clamp01((chromeTop + rect.top) / totalH),
        w: clamp01(rect.width / totalW),
        h: clamp01(rect.height / totalH),
    };
}
/**
 * OV-2/OV-4: is the camera tile actually visible on the shared screen right now?
 * False when the tab is hidden (candidate switched to another tab/app — the PiP
 * isn't painted on the recording), or the camera is off / has no stream. Drives
 * the `camVisible` flag on the emitted layout so the report overlay hides for
 * exactly those spans.
 */
export function cameraTileVisible(documentHidden, camEnabled, hasStream) {
    return !documentHidden && !!camEnabled && !!hasStream;
}
/**
 * OV-3: map a tile's viewport rect to a fraction of the WHOLE SCREEN using the
 * window's on-screen geometry. The screen share captures the entire monitor, so
 * when the browser is NOT fullscreen/maximized the recording is the monitor and
 * the tile sits at the window's screen offset — which `viewportToScreenFraction`
 * (viewport-only) cannot know, so its overlay drifts once fullscreen is exited.
 *
 * Geometry (all CSS pixels, so the fraction stays DPR/zoom-invariant — the
 * recording scales uniformly):
 *   • viewport screen origin = (screenX, screenY)
 *   • tile screen pos = origin + rect;  fraction = pos / screen size
 *
 * CRITICAL: `screenX`/`screenY` are, per CSSOM View, the position of the
 * VIEWPORT (client area) relative to the screen — they ALREADY exclude the
 * browser chrome (title/tab/address/bookmarks bars). Do NOT add
 * `outerH − innerH` back on top: that double-counts the chrome and shifts the
 * tile (and every landmark) DOWN by the chrome height whenever the window is
 * not fullscreen. The bug was invisible in fullscreen only because chrome ≈ 0
 * and screenX/Y ≈ 0 there.
 *
 * Subsumes the fullscreen/maximized case (screenX≈0, screenY≈0 → pure viewport
 * fraction). Best-effort on a secondary monitor (screenX/Y are virtual-desktop
 * coords that can exceed the primary screen); fractions are clamped to [0,1].
 * Returns null when screen metrics are unusable so the caller can fall back to
 * the viewport-only mapping.
 */
export function screenRectFraction(rect, m) {
    if (!m.screenW || !m.screenH || rect.width <= 0 || rect.height <= 0)
        return null;
    // screenX/screenY are the viewport origin on the screen (chrome already
    // excluded) — see the note above.
    const originX = m.screenX;
    const originY = m.screenY;
    return {
        x: clamp01((originX + rect.left) / m.screenW),
        y: clamp01((originY + rect.top) / m.screenH),
        w: clamp01(rect.width / m.screenW),
        h: clamp01(rect.height / m.screenH),
    };
}
/**
 * OV: object-fit placement of a source frame inside a tile.
 *
 * Detection landmarks are normalized (0..1) to the RAW webcam frame, but the
 * on-screen tile shows that frame under `object-fit` — so with a mismatched
 * aspect ratio the frame is cropped (cover) or letterboxed (contain) and the
 * displayed content occupies only a sub-region of the tile. This returns the
 * source frame's displayed rect in TILE-normalized units (centered), so a
 * landmark maps as:  tx = ox + nx*sw,  ty = oy + ny*sh.
 *
 * With `cover` the overflowing axis has size > 1 and a negative offset (the
 * cropped part sits outside [0,1]); with `contain` the short axis has size < 1
 * and a positive offset (letterbox bars). Identity {0,0,1,1} when either aspect
 * is unusable — legacy behaviour (stretch to fill).
 */
export function objectFitSourceRect(fit, srcAspect, // srcW / srcH
tileAspect) {
    if (!(srcAspect > 0) || !(tileAspect > 0))
        return { ox: 0, oy: 0, sw: 1, sh: 1 };
    const ratio = tileAspect / srcAspect;
    let sw = 1;
    let sh = 1;
    if (fit === 'contain') {
        if (ratio >= 1)
            sw = 1 / ratio; // tile wider than source → pillarbox (bars L/R)
        else
            sh = ratio; //                tile taller than source → letterbox (bars T/B)
    }
    else {
        // cover
        if (ratio >= 1)
            sh = ratio; //     tile wider than source → crop top/bottom
        else
            sw = 1 / ratio; //            tile taller than source → crop left/right
    }
    return { ox: (1 - sw) / 2, oy: (1 - sh) / 2, sw, sh };
}
/** Last layout transition at or before playback time `t` (earliest if before all). */
export function findLayoutAt(rows, t) {
    if (!rows || !rows.length)
        return null;
    if (t < rows[0].t)
        return rows[0];
    let lo = 0;
    let hi = rows.length - 1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (rows[mid].t <= t)
            lo = mid + 1;
        else
            hi = mid - 1;
    }
    return rows[hi] || rows[0];
}
/**
 * P4: compose a WEBCAM-normalized landmark (0..1 of the camera frame) into a
 * SCREEN-normalized point (0..1 of the recording frame), applying the layout
 * row's mirror flip and camera-box placement.
 *
 * Returns null when the camera is not visible on the shared surface (overlay
 * should draw nothing). With no box (legacy data) it falls back to identity so
 * old reports — whose landmarks were already in screen space — still render.
 */
export function composeToScreenFraction(nx, ny, row) {
    if (row && row.camVisible === false)
        return null;
    const box = row === null || row === void 0 ? void 0 : row.camBox;
    const mirrored = !!(row === null || row === void 0 ? void 0 : row.mirrored);
    const hasBox = !!(box && box.w > 0 && box.h > 0);
    const mx = mirrored ? 1 - nx : nx;
    return {
        x: hasBox ? box.x + mx * box.w : mx,
        y: hasBox ? box.y + ny * box.h : ny,
    };
}
/** True when two boxes are equal within a small epsilon (avoids resize jitter). */
export function camBoxEquals(a, b, eps = 0.002) {
    if (!a || !b)
        return a === b;
    return (Math.abs(a.x - b.x) < eps &&
        Math.abs(a.y - b.y) < eps &&
        Math.abs(a.w - b.w) < eps &&
        Math.abs(a.h - b.h) < eps);
}
/** Read the mirror state of a tile element from its computed transform. */
export function elementIsMirrored(el) {
    if (!el || typeof window === 'undefined' || !window.getComputedStyle)
        return false;
    const t = window.getComputedStyle(el).transform || '';
    // matrix(a, b, c, d, e, f): a < 0 => horizontally flipped (scaleX(-1)).
    const m = t.match(/matrix\(([^)]+)\)/);
    if (m) {
        const a = parseFloat(m[1].split(',')[0]);
        if (!Number.isNaN(a) && a < 0)
            return true;
    }
    return false;
}
/** Read object-fit ('cover' | 'contain') of a tile element. */
export function elementFit(el) {
    if (!el || typeof window === 'undefined' || !window.getComputedStyle)
        return 'cover';
    return window.getComputedStyle(el).objectFit === 'contain' ? 'contain' : 'cover';
}
/**
 * Build a full proctor_layout payload from a live camera tile element.
 * Returns null when the tile isn't rendered/visible (caller emits
 * camVisible:false separately in that case).
 */
export function buildCameraLayout(el, ts) {
    if (!el || typeof window === 'undefined')
        return null;
    // offsetParent === null => not rendered (display:none / detached).
    if (el.offsetParent === null && el.getClientRects().length === 0)
        return null;
    const r = el.getBoundingClientRect();
    const rect = { left: r.left, top: r.top, width: r.width, height: r.height };
    const innerW = window.innerWidth || 0;
    const innerH = window.innerHeight || 0;
    const outerW = window.outerWidth || innerW;
    const outerH = window.outerHeight || innerH;
    // OV-3: prefer the SCREEN-relative mapping (uses the window's on-screen origin),
    // which is correct whether the browser is fullscreen, maximized, OR a floating
    // window somewhere on the monitor — the screen share captures the whole monitor.
    // Fall back to the viewport-only mapping when screen metrics are unavailable
    // (e.g. SSR / a non-"entire screen" share where the viewport fills the surface).
    const screen = typeof window !== 'undefined' ? window.screen : undefined;
    const camBox = screenRectFraction(rect, {
        innerW,
        innerH,
        outerW,
        outerH,
        screenX: typeof window.screenX === 'number' ? window.screenX : 0,
        screenY: typeof window.screenY === 'number' ? window.screenY : 0,
        screenW: (screen === null || screen === void 0 ? void 0 : screen.width) || 0,
        screenH: (screen === null || screen === void 0 ? void 0 : screen.height) || 0,
    }) || viewportToScreenFraction(rect, { innerW, innerH, outerW, outerH });
    if (!camBox)
        return null;
    // Native webcam frame size — only meaningful for a <video> tile. Lets the
    // report overlay reproduce the tile's object-fit crop so landmarks aren't
    // stretched across the whole tile when camera aspect ≠ tile aspect.
    const vid = el;
    const srcW = typeof vid.videoWidth === 'number' && vid.videoWidth > 0 ? vid.videoWidth : undefined;
    const srcH = typeof vid.videoHeight === 'number' && vid.videoHeight > 0 ? vid.videoHeight : undefined;
    return Object.assign({ ts: Math.max(0, ts), camBox, mirrored: elementIsMirrored(el), camVisible: true, fit: elementFit(el) }, (srcW && srcH ? { srcW, srcH } : {}));
}
